import {useCallback, useEffect} from 'react';

import {assert} from 'Asserts';
import {FORMATTING_SHORTCUTS} from 'components/KeyboardShortcutDescriptions';
import {ActionMethod, ActionOrigin} from 'constants/AnalyticsConstants';
import formatters from 'notebook/mediocre/formatted-text/formatters';
import FormattersConstants from 'notebook/mediocre/formatted-text/FormattersConstants';
import {trackKeyboardShortcut} from 'notebook/util/analytics/KeyboardShortcutAnalytics';
import {ctrl} from 'util/Keyboard';

export function useFormatShortcut(
  format: string,
  onActivate: (format: string) => void,
  {disabled}: {disabled?: boolean} = {},
  metadata?: {
    isHighlightedCellEmpty: boolean;
    eventGroupId?: string;
  }
): string {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent): void => {
      const eventFormat = formatters.formatTypeFromKeydown(event);
      if (eventFormat !== format) {
        return;
      }
      // Track usage of formatting shortcuts
      const shortcutDesc = FORMATTING_SHORTCUTS.find(
        (s) => s.description.toLowerCase() === format.toLowerCase()
      );
      if (shortcutDesc) {
        if (format === FormattersConstants.LINK) {
          trackKeyboardShortcut(shortcutDesc, {
            actionOrigin: ActionOrigin.NOTEBOOK_ANY_TABLE,
            actionMethod: ActionMethod.OPEN_MODAL,
            // Default text selected is false (if metadata is undefined).
            wasTextSelected: !(metadata?.isHighlightedCellEmpty ?? true),
            eventGroupId: metadata?.eventGroupId,
          });
        } else {
          trackKeyboardShortcut(shortcutDesc);
        }
      }
      onActivate(format);
      event.preventDefault();
    },
    [format, onActivate, metadata?.isHighlightedCellEmpty, metadata?.eventGroupId]
  );

  useEffect(() => {
    if (disabled) {
      return;
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, disabled]);

  const shortcut = formatters.getFormatShortcut(format);
  assert(shortcut != null, `No shortcut found for format ${format}`);
  return shortcut.replace('Ctrl', ctrl);
}
